import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { User, Bell, Shield, Bot, Loader2, Eye, EyeOff, Check, X, Trash2, AlertTriangle, ArrowRightLeft, MessageSquare, ChevronDown, ChevronUp, Download, Upload } from 'lucide-react';
import { api } from '@/lib/api';

interface ConversationLog {
  id: string;
  userMessage: string;
  assistantResponse: string;
  provider: string;
  actions: Array<{
    tool: string;
    input: any;
    result: {
      success: boolean;
      message: string;
      data?: any;
    };
  }>;
  year: number;
  createdAt: string;
}

const filingStatuses = [
  { value: 'single', label: 'Single' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'head_of_household', label: 'Head of Household' },
  { value: 'qualifying_widow', label: 'Qualifying Widow(er)' },
];

export function SettingsPage() {
  const { user } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: user?.name || '',
    filingStatus: user?.settings?.filingStatus || 'single',
    darkMode: user?.settings?.darkMode ?? true,
    emailNotifications: true,
  });

  // AI Settings state
  const [aiSettings, setAiSettings] = useState({
    anthropicApiKey: '',
    openaiApiKey: '',
    preferredAiProvider: 'anthropic' as 'anthropic' | 'openai',
    aiContextEnabled: true,
    hasAnthropicKey: false,
    hasOpenaiKey: false,
  });
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [loadingAi, setLoadingAi] = useState(true);
  const [savingAi, setSavingAi] = useState(false);
  const [aiMessage, setAiMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Conversation history state
  const [conversations, setConversations] = useState<ConversationLog[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedConversation, setExpandedConversation] = useState<string | null>(null);

  // Load AI settings on mount
  useEffect(() => {
    const loadAiSettings = async () => {
      try {
        const response = await api.getSettings();
        setAiSettings({
          anthropicApiKey: '',
          openaiApiKey: '',
          preferredAiProvider: response.settings.preferredAiProvider || 'anthropic',
          aiContextEnabled: response.settings.aiContextEnabled ?? true,
          hasAnthropicKey: response.settings.hasAnthropicKey,
          hasOpenaiKey: response.settings.hasOpenaiKey,
        });
      } catch (err) {
        console.error('Error loading AI settings:', err);
      }
      setLoadingAi(false);
    };
    loadAiSettings();
  }, []);

  // Load conversation history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await api.getAiHistory({ limit: 50 });
        setConversations(response.conversations);
      } catch (err) {
        console.error('Error loading conversation history:', err);
      }
      setLoadingHistory(false);
    };
    loadHistory();
  }, []);

  const handleSaveAi = async () => {
    setSavingAi(true);
    setAiMessage(null);
    try {
      const updateData: any = {
        preferredAiProvider: aiSettings.preferredAiProvider,
        aiContextEnabled: aiSettings.aiContextEnabled,
      };

      // Only send API keys if they're being changed (not masked placeholder)
      if (aiSettings.anthropicApiKey && !aiSettings.anthropicApiKey.includes('****')) {
        updateData.anthropicApiKey = aiSettings.anthropicApiKey;
      }
      if (aiSettings.openaiApiKey && !aiSettings.openaiApiKey.includes('****')) {
        updateData.openaiApiKey = aiSettings.openaiApiKey;
      }

      const response = await api.updateSettings(updateData);
      setAiSettings({
        ...aiSettings,
        anthropicApiKey: '',
        openaiApiKey: '',
        hasAnthropicKey: response.settings.hasAnthropicKey,
        hasOpenaiKey: response.settings.hasOpenaiKey,
      });
      setAiMessage({ type: 'success', text: 'AI settings saved successfully!' });
    } catch (err: any) {
      setAiMessage({ type: 'error', text: err.message || 'Failed to save AI settings' });
    }
    setSavingAi(false);
  };

  const handleClearKey = async (provider: 'anthropic' | 'openai') => {
    setSavingAi(true);
    try {
      const updateData = provider === 'anthropic'
        ? { clearAnthropicKey: true }
        : { clearOpenaiKey: true };
      const response = await api.updateSettings(updateData);
      setAiSettings({
        ...aiSettings,
        hasAnthropicKey: response.settings.hasAnthropicKey,
        hasOpenaiKey: response.settings.hasOpenaiKey,
      });
      setAiMessage({ type: 'success', text: `${provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API key removed` });
    } catch (err: any) {
      setAiMessage({ type: 'error', text: err.message || 'Failed to remove API key' });
    }
    setSavingAi(false);
  };

  const handleSave = async () => {
    setSaving(true);
    // In a real app, this would call the API
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSaving(false);
  };

  // Reset data state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState<{
    type: 'success' | 'error';
    text: string;
    details?: any;
  } | null>(null);

  // Year migration state
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const [migrateFromYear, setMigrateFromYear] = useState(currentYear);
  const [migrateToYear, setMigrateToYear] = useState(currentYear - 1);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<{
    type: 'success' | 'error';
    text: string;
    details?: any;
  } | null>(null);

  // Excel export state
  const [exportYear, setExportYear] = useState(currentYear);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Excel import state
  const [importYear, setImportYear] = useState(currentYear);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    type: 'success' | 'error';
    text: string;
    details?: any;
  } | null>(null);

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip data URL prefix to get raw base64
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await api.reimportExcel({ fileData: base64, year: importYear });
      const details = result.imported;
      const total = Object.values(details).reduce((a, b) => a + b, 0);
      setImportResult({
        type: 'success',
        text: `Imported ${total} records for ${importYear}`,
        details,
      });
    } catch (err: any) {
      setImportResult({ type: 'error', text: err.message || 'Failed to import data' });
    }

    setImporting(false);
    // Reset file input
    e.target.value = '';
  };

  const handleExportExcel = async () => {
    setExporting(true);
    setExportResult(null);
    try {
      await api.exportExcel(exportYear);
      setExportResult({ type: 'success', text: `Exported ${exportYear} tax data as Excel` });
    } catch (err: any) {
      setExportResult({ type: 'error', text: err.message || 'Failed to export data' });
    }
    setExporting(false);
  };

  const handleMigrateYear = async () => {
    if (migrateFromYear === migrateToYear) {
      setMigrateResult({ type: 'error', text: 'Source and destination years must be different' });
      return;
    }
    setMigrating(true);
    setMigrateResult(null);
    try {
      const result = await api.migrateYear(migrateFromYear, migrateToYear);
      setMigrateResult({
        type: 'success',
        text: result.message,
        details: result.migrated,
      });
    } catch (err: any) {
      setMigrateResult({
        type: 'error',
        text: err.message || 'Failed to migrate data',
      });
    }
    setMigrating(false);
  };

  const handleResetData = async () => {
    setResetting(true);
    setResetResult(null);
    try {
      const result = await api.resetData();
      setResetResult({
        type: 'success',
        text: 'All data has been reset successfully!',
        details: result.deleted,
      });
      setShowResetConfirm(false);
    } catch (err: any) {
      setResetResult({
        type: 'error',
        text: err.message || 'Failed to reset data',
      });
    }
    setResetting(false);
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Profile */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <User className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Profile</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => setSettings({ ...settings, name: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Default Filing Status</label>
            <select
              value={settings.filingStatus}
              onChange={(e) => setSettings({ ...settings, filingStatus: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {filingStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Preferences</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">Use dark theme</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, darkMode: !settings.darkMode })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.darkMode ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings.darkMode ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">Receive tax deadline reminders</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.emailNotifications ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings.emailNotifications ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* AI Assistant */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bot className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">AI Assistant</h3>
        </div>

        {loadingAi ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {aiMessage && (
              <div
                className={`p-3 rounded-lg flex items-center gap-2 ${
                  aiMessage.type === 'success'
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                {aiMessage.type === 'success' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                <span className="text-sm">{aiMessage.text}</span>
              </div>
            )}

            {/* Anthropic API Key */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Anthropic API Key (Claude)
                {aiSettings.hasAnthropicKey && (
                  <span className="ml-2 text-xs text-green-500">Configured</span>
                )}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showAnthropicKey ? 'text' : 'password'}
                    value={aiSettings.anthropicApiKey}
                    onChange={(e) =>
                      setAiSettings({ ...aiSettings, anthropicApiKey: e.target.value })
                    }
                    className="w-full px-3 py-2 pr-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={aiSettings.hasAnthropicKey ? '••••••••' : 'sk-ant-...'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showAnthropicKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {aiSettings.hasAnthropicKey && (
                  <button
                    onClick={() => handleClearKey('anthropic')}
                    className="px-3 py-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    title="Remove key"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Get your key at{' '}
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  console.anthropic.com
                </a>
              </p>
            </div>

            {/* OpenAI API Key */}
            <div>
              <label className="block text-sm font-medium mb-2">
                OpenAI API Key (GPT-4)
                {aiSettings.hasOpenaiKey && (
                  <span className="ml-2 text-xs text-green-500">Configured</span>
                )}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showOpenaiKey ? 'text' : 'password'}
                    value={aiSettings.openaiApiKey}
                    onChange={(e) =>
                      setAiSettings({ ...aiSettings, openaiApiKey: e.target.value })
                    }
                    className="w-full px-3 py-2 pr-10 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={aiSettings.hasOpenaiKey ? '••••••••' : 'sk-...'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showOpenaiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {aiSettings.hasOpenaiKey && (
                  <button
                    onClick={() => handleClearKey('openai')}
                    className="px-3 py-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    title="Remove key"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Get your key at{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  platform.openai.com
                </a>
              </p>
            </div>

            {/* Preferred Provider */}
            <div>
              <label className="block text-sm font-medium mb-2">Preferred AI Provider</label>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setAiSettings({ ...aiSettings, preferredAiProvider: 'anthropic' })
                  }
                  className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                    aiSettings.preferredAiProvider === 'anthropic'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  Claude (Anthropic)
                </button>
                <button
                  onClick={() =>
                    setAiSettings({ ...aiSettings, preferredAiProvider: 'openai' })
                  }
                  className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                    aiSettings.preferredAiProvider === 'openai'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  GPT-4 (OpenAI)
                </button>
              </div>
            </div>

            {/* Context Toggle */}
            <div className="flex items-center justify-between py-3 border-t border-border">
              <div>
                <p className="font-medium">Include Tax Context</p>
                <p className="text-sm text-muted-foreground">
                  Share your tax data with AI for personalized answers
                </p>
              </div>
              <button
                onClick={() =>
                  setAiSettings({
                    ...aiSettings,
                    aiContextEnabled: !aiSettings.aiContextEnabled,
                  })
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  aiSettings.aiContextEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    aiSettings.aiContextEnabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {/* Save AI Settings Button */}
            <button
              onClick={handleSaveAi}
              disabled={savingAi}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
            >
              {savingAi ? (
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              ) : (
                'Save AI Settings'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Conversation History */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">AI Conversation History</h3>
        </div>

        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : conversations.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No conversations yet. Chat with the AI assistant to see your history here.
          </p>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className="border border-border rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedConversation(expandedConversation === conv.id ? null : conv.id)}
                  className="w-full p-3 flex items-start justify-between hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{conv.userMessage}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {new Date(conv.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 bg-muted rounded capitalize">
                        {conv.provider}
                      </span>
                      {conv.actions && conv.actions.length > 0 && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-500/10 text-green-500 rounded">
                          {conv.actions.length} action{conv.actions.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {expandedConversation === conv.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                  )}
                </button>

                {expandedConversation === conv.id && (
                  <div className="p-3 pt-0 border-t border-border bg-muted/30">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Your message:</p>
                        <p className="text-sm bg-background p-2 rounded">{conv.userMessage}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">AI response:</p>
                        <p className="text-sm bg-background p-2 rounded whitespace-pre-wrap">{conv.assistantResponse}</p>
                      </div>
                      {conv.actions && conv.actions.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Actions taken:</p>
                          <div className="space-y-1">
                            {conv.actions.map((action, idx) => (
                              <div
                                key={idx}
                                className={`text-xs p-2 rounded ${
                                  action.result.success
                                    ? 'bg-green-500/10 text-green-500'
                                    : 'bg-destructive/10 text-destructive'
                                }`}
                              >
                                <span className="font-medium">{action.tool}</span>:{' '}
                                {action.result.message}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Security</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">Currently using email OTP</p>
            </div>
            <span className="px-2 py-1 bg-green-500/10 text-green-500 text-sm rounded-lg">
              Enabled
            </span>
          </div>

          <div className="py-3">
            <p className="font-medium mb-2">Active Sessions</p>
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium">Current Session</p>
              <p className="text-xs text-muted-foreground">This device</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Data Management</h3>

        {/* Year Migration */}
        <div className="p-4 bg-muted/30 border border-border rounded-lg mb-4">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRightLeft className="h-5 w-5 text-primary" />
            <span className="font-medium">Migrate Data Between Years</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Move all expenses, income, investments, deductions, and documents from one tax year to another.
          </p>

          {migrateResult && (
            <div
              className={`p-3 rounded-lg mb-4 ${
                migrateResult.type === 'success'
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-destructive/10 text-destructive'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {migrateResult.type === 'success' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                <span className="font-medium">{migrateResult.text}</span>
              </div>
              {migrateResult.details && (
                <div className="text-sm opacity-80 ml-6">
                  Migrated: {migrateResult.details.expenses} expenses, {migrateResult.details.income} income, {migrateResult.details.investments} investments, {migrateResult.details.deductions} deductions, {migrateResult.details.documents} documents
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground mb-1">From Year</label>
              <select
                value={migrateFromYear}
                onChange={(e) => setMigrateFromYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <ArrowRightLeft className="h-5 w-5 text-muted-foreground mt-5" />
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground mb-1">To Year</label>
              <select
                value={migrateToYear}
                onChange={(e) => setMigrateToYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleMigrateYear}
            disabled={migrating || migrateFromYear === migrateToYear}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {migrating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <ArrowRightLeft className="h-4 w-4" />
                Migrate Data
              </>
            )}
          </button>
        </div>

        {resetResult && (
          <div
            className={`p-3 rounded-lg mb-4 ${
              resetResult.type === 'success'
                ? 'bg-green-500/10 text-green-500'
                : 'bg-destructive/10 text-destructive'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              {resetResult.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <X className="h-4 w-4" />
              )}
              <span className="font-medium">{resetResult.text}</span>
            </div>
            {resetResult.details && (
              <div className="text-sm opacity-80 ml-6">
                Deleted: {resetResult.details.documents} documents, {resetResult.details.businesses} businesses, {resetResult.details.expenses} expenses, {resetResult.details.income} income records, {resetResult.details.investments} investments, {resetResult.details.deductions} deductions
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {/* Export Data */}
          <div className="p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Download className="h-5 w-5 text-primary" />
              <span className="font-medium">Export Data as Excel</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Download all your tax data as an Excel spreadsheet to share with your tax preparer. Includes income, expenses, deductions, investments, and more.
            </p>

            {exportResult && (
              <div
                className={`p-3 rounded-lg mb-3 flex items-center gap-2 ${
                  exportResult.type === 'success'
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                {exportResult.type === 'success' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                <span className="text-sm">{exportResult.text}</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs text-muted-foreground mb-1">Tax Year</label>
                <select
                  value={exportYear}
                  onChange={(e) => setExportYear(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                className="mt-5 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center gap-2"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Import Data */}
          <div className="p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="h-5 w-5 text-primary" />
              <span className="font-medium">Import Data from Excel</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Re-import data from a previously exported TaxVault Excel file. This will add records for income, expenses, deductions, investments, and tasks.
            </p>

            {importResult && (
              <div
                className={`p-3 rounded-lg mb-3 ${
                  importResult.type === 'success'
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {importResult.type === 'success' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                  <span className="text-sm font-medium">{importResult.text}</span>
                </div>
                {importResult.details && (
                  <div className="text-xs opacity-80 ml-6">
                    {importResult.details.businesses > 0 && `${importResult.details.businesses} businesses, `}
                    {importResult.details.income > 0 && `${importResult.details.income} income, `}
                    {importResult.details.expenses > 0 && `${importResult.details.expenses} expenses, `}
                    {importResult.details.deductions > 0 && `${importResult.details.deductions} deductions, `}
                    {importResult.details.investments > 0 && `${importResult.details.investments} investments, `}
                    {importResult.details.tasks > 0 && `${importResult.details.tasks} tasks`}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs text-muted-foreground mb-1">Tax Year</label>
                <select
                  value={importYear}
                  onChange={(e) => setImportYear(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <label className="mt-5 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth cursor-pointer flex items-center gap-2">
                {importing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Import
                  </>
                )}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  disabled={importing}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Reset Data Button */}
          {!showResetConfirm ? (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="w-full px-4 py-2 bg-yellow-500/10 text-yellow-500 rounded-lg font-medium hover:bg-yellow-500/20 transition-smooth text-left flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Reset All Data
            </button>
          ) : (
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-500 mb-3">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Confirm Data Reset</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                This will permanently delete all your documents, businesses, expenses, income, investments, and deductions. Your account will remain but all tax data will be removed. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetData}
                  disabled={resetting}
                  className="flex-1 px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-600 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {resetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Reset Now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <button className="w-full px-4 py-2 bg-destructive/10 text-destructive rounded-lg font-medium hover:bg-destructive/20 transition-smooth text-left">
            Delete Account
          </button>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
      >
        {saving ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        ) : (
          'Save Settings'
        )}
      </button>
    </div>
  );
}
